import { Suspense, lazy, ReactNode } from "react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoadingFallback from "@/components/ui/LoadingFallback";
import CommandMenu from "@/components/shared/CommandMenu";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AuthErrorBoundary } from "@/components/AuthErrorBoundary";

// Wrapper component for lazy-loaded routes with error boundary and suspense
interface LazyRouteWrapperProps {
  children: ReactNode;
}

const LazyRouteWrapper = ({ children }: LazyRouteWrapperProps) => (
  <ErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      {children}
    </Suspense>
  </ErrorBoundary>
);

// Lazy load pages for performance optimization
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const StaffDashboard = lazy(() => import("./pages/StaffDashboard"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const TasksPage = lazy(() => import("./pages/TasksPage"));
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const ClientDocumentsPage = lazy(() => import("./pages/ClientDocumentsPage"));
const ClientTasksPage = lazy(() => import("./pages/ClientTasksPage"));
const ClientInvoicesPage = lazy(() => import("./pages/ClientInvoicesPage"));
const ClientUserProfile = lazy(() => import("./pages/ClientUserProfile"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Unauthorized = lazy(() => import("./pages/Unauthorized"));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const GSTRegistration = lazy(() => import("./pages/GSTRegistration"));
const GSTLogin = lazy(() => import("./pages/GSTLogin"));
const GSTReports = lazy(() => import("./pages/GSTReports"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const UiSmoke = lazy(() => import("./pages/UiSmoke"));

const InvoicePrintPage = lazy(() => import("./pages/admin/InvoicePrintPage"));
const TallyIntegration = lazy(() => import("./pages/TallyIntegration"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
      retry: 1, // Retry failed requests once

      // Global default: Don't refetch on window focus
      // This prevents unnecessary network requests for non-critical data
      // Critical queries (clients, invoices, tasks) override this at query level
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0, // Don't retry failed mutations
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <SpeedInsights />
      <BrowserRouter>
        <AuthErrorBoundary>
          <AuthProvider>
            <ErrorBoundary>
              <CommandMenu />
              <Routes>
                {/* Public Routes with error boundary for lazy loading */}
                <Route
                  path="/"
                  element={
                    <LazyRouteWrapper>
                      <Index />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/auth"
                  element={
                    <LazyRouteWrapper>
                      <Auth />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/reset-password"
                  element={
                    <LazyRouteWrapper>
                      <ResetPassword />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/unauthorized"
                  element={
                    <LazyRouteWrapper>
                      <Unauthorized />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/maintenance"
                  element={
                    <LazyRouteWrapper>
                      <MaintenancePage />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/privacy-policy"
                  element={
                    <LazyRouteWrapper>
                      <PrivacyPolicy />
                    </LazyRouteWrapper>
                  }
                />

                {/* Protected Routes */}
                <Route
                  path="/admin-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                      <LazyRouteWrapper>
                        <AdminDashboard />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/staff-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['staff']}>
                      <LazyRouteWrapper>
                        <StaffDashboard />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-dashboard"
                  element={
                    <ProtectedRoute allowedRoles={['client']}>
                      <LazyRouteWrapper>
                        <ClientDashboard />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/tasks"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'super_admin', 'staff']}>
                      <LazyRouteWrapper>
                        <TasksPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'super_admin', 'staff']}>
                      <LazyRouteWrapper>
                        <DocumentsPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-documents"
                  element={
                    <ProtectedRoute allowedRoles={['client']}>
                      <LazyRouteWrapper>
                        <ClientDocumentsPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-tasks"
                  element={
                    <ProtectedRoute allowedRoles={['client']}>
                      <LazyRouteWrapper>
                        <ClientTasksPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/client-invoices"
                  element={
                    <ProtectedRoute allowedRoles={['client']}>
                      <LazyRouteWrapper>
                        <ClientInvoicesPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute allowedRoles={['client']}>
                      <LazyRouteWrapper>
                        <ClientUserProfile />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <ProtectedRoute>
                      <LazyRouteWrapper>
                        <NotificationsPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/__ui-smoke"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'super_admin', 'staff', 'client']}>
                      <LazyRouteWrapper>
                        <UiSmoke />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/invoices/:id/print"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                      <LazyRouteWrapper>
                        <InvoicePrintPage />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/tally-integration"
                  element={
                    <ProtectedRoute allowedRoles={['admin', 'super_admin', 'staff']}>
                      <LazyRouteWrapper>
                        <TallyIntegration />
                      </LazyRouteWrapper>
                    </ProtectedRoute>
                  }
                />

                {/* GST Portal Routes */}
                <Route
                  path="/gst-registration"
                  element={
                    <LazyRouteWrapper>
                      <GSTRegistration />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/gst-login"
                  element={
                    <LazyRouteWrapper>
                      <GSTLogin />
                    </LazyRouteWrapper>
                  }
                />
                <Route
                  path="/gst-reports"
                  element={
                    <LazyRouteWrapper>
                      <GSTReports />
                    </LazyRouteWrapper>
                  }
                />

                <Route
                  path="*"
                  element={
                    <LazyRouteWrapper>
                      <NotFound />
                    </LazyRouteWrapper>
                  }
                />
              </Routes>
            </ErrorBoundary>
          </AuthProvider>
        </AuthErrorBoundary>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
